import { KeyRound } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import type { PluginAttachmentValue } from '@/lib/types';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useAttachmentPickerProviders } from '@/hooks/config/useAttachmentPickerProviders';
import { AttachmentChip } from './attachments/AttachmentChip';

interface AgentAttachmentsEditorProps {
  attachments: Record<string, PluginAttachmentValue>;
  /**
   * Called with either a fresh config (the picker's own onChange result) or
   * `null` to remove the attachment entirely. Matches the existing
   * `set_attachment` reducer shape in `LaunchAgentDialog`.
   */
  onAttachmentChange: (id: string, config: unknown | null, pluginName: string) => void;
  className?: string;
}

/**
 * Compact chip bar for plugin-contributed attachments declared via
 * `PluginWebManifest.attachments`. Every available attachment kind renders as
 * its own {@link AttachmentChip} (icon + label with a click-popover hosting the
 * plugin's picker), dashed until configured — so the bar reads as a row of
 * named per-kind chips instead of a single "+ Add" menu. Environment variables
 * and secrets now live in the Advanced bar, so this editor covers plugin
 * attachments only. Shared between the agent launch surfaces and the persona
 * form so both stay in sync as new plugin attachment kinds appear.
 */
export function AgentAttachmentsEditor({
  attachments,
  onAttachmentChange,
  className,
}: AgentAttachmentsEditorProps) {
  const { attachments: resolvedAttachments } = usePlugins();
  // Provider info for attachment pickers that gate on OAuth connections. Shared
  // with the launch composer's applied-context chips so both hosts gate
  // identically.
  const { providers, connectProvider, connectingProvider } = useAttachmentPickerProviders();

  return (
    <div
      className={cn('flex flex-wrap items-end gap-x-4 gap-y-3', className)}
      data-testid="agent-attachments-editor"
    >
      {resolvedAttachments.length === 0 ? (
        <p data-testid="agent-attachments-empty-hint" className="text-2xs text-muted-foreground">
          No attachments available.
        </p>
      ) : (
        resolvedAttachments.map((att) => (
          <AttachmentChip
            key={att.id}
            definition={att}
            value={attachments[att.id]}
            providers={providers}
            connectProvider={connectProvider}
            connectingProvider={connectingProvider}
            onConfigChange={(config) => onAttachmentChange(att.id, config, att.pluginName)}
            onRemove={() => onAttachmentChange(att.id, null, att.pluginName)}
          />
        ))
      )}
    </div>
  );
}

/** Sentinel icon kept here for parity with `AgentEnvVarsIcon`. */
export const AgentAttachmentsIcon = KeyRound;
